import React, {useEffect} from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import CheckableReviewCriteriaGroup from 'notebook/reviews/CheckableReviewCriteriaGroup';
import {mapReviewCriteriaGroupsToProps} from 'notebook/reviews/reduceReviewCriteria';
import {ReviewCriterion, ReviewCriterionStatus} from 'notebook/reviews/ReviewCriteriaConstants';
import {fetchReviewCriteria, updateReviewCriterion} from 'notebook/reviews/ReviewCriteriaReduxActions';
import ReviewLabelText from 'notebook/reviews/ReviewLabelText';
import * as ReviewRoles from 'notebook/reviews/ReviewRoles';
import {BenchlingState} from 'redux-stores/reduceApp';
import {Thunk} from 'redux-stores/Store';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ReviewLifecycleReviewersQuery,
  ReviewLifecycleReviewersQueryVariables,
} from './__generated/ReviewLifecycleReviewersQuery';
import {REVIEW_LIFECYCLE_REVIEWERS_QUERY} from './Review';
import {ReviewerUser, ReviewSummary} from './ReviewConstants';

interface ConnectedProps {
  reviewCriteriaGroups: Array<{
    id: string | undefined;
    name: string;
    reviewCriteria: Array<ReviewCriterion>;
  }> | null;
}

export function mapStateToProps(
  state: BenchlingState,
  {linkedItemId}: BaseReviewCriteriaSectionProps
): ConnectedProps {
  return {
    reviewCriteriaGroups: mapReviewCriteriaGroupsToProps(state, linkedItemId),
  };
}

interface DispatchProps {
  fetchReviewCriteria: (linkedItemId: string) => Thunk<void>;
  updateReviewCriterion: (criterionId: string, {status}: {status: ReviewCriterionStatus}) => Thunk<void>;
}

interface BaseReviewCriteriaSectionProps {
  linkedItemId: string;
  review: ReviewSummary;
}

type ReviewCriteriaSectionProps = BaseReviewCriteriaSectionProps & ConnectedProps & DispatchProps;

export function ReviewCriteriaSection({
  fetchReviewCriteria,
  linkedItemId,
  review,
  reviewCriteriaGroups,
  updateReviewCriterion,
}: ReviewCriteriaSectionProps): JSX.Element {
  useEffect(() => {
    fetchReviewCriteria(linkedItemId);
  }, [fetchReviewCriteria, linkedItemId]);

  let reviewers = review.auditors;

  const result = useBenchlingQuery<ReviewLifecycleReviewersQuery, ReviewLifecycleReviewersQueryVariables>(
    REVIEW_LIFECYCLE_REVIEWERS_QUERY,
    {
      variables: {reviewId: review.id},
    }
  );

  if (result.type === QueryResultType.SUCCESS) {
    const reviewGraphQL = result.data.review;
    assert(
      !isInaccessible(reviewGraphQL),
      "User shouldn't be able to access a review tab if it's inaccessible"
    );
    if (reviewGraphQL.lastUsedReviewLifecycleVersion != null && reviewGraphQL.reviewers) {
      reviewers = reviewGraphQL.reviewers.map((reviewerNode) => reviewerNode.user as unknown as ReviewerUser);
    }
  }

  return (
    <Row>
      <Col sm={12}>
        <ReviewLabelText>Review</ReviewLabelText>
        {!reviewCriteriaGroups || result.type === QueryResultType.PENDING ? (
          <CenteredHorizontalThrobber />
        ) : (
          <>
            {reviewCriteriaGroups.map((reviewCriteriaGroup) => (
              <CheckableReviewCriteriaGroup
                key={reviewCriteriaGroup.id}
                review={review}
                reviewCriteriaGroup={reviewCriteriaGroup}
                updateReviewCriterion={updateReviewCriterion}
                enabled={ReviewRoles.isReviewer(reviewers)}
              />
            ))}
          </>
        )}
      </Col>
    </Row>
  );
}

export default connect(mapStateToProps, {fetchReviewCriteria, updateReviewCriterion})(ReviewCriteriaSection);
